// src/lib/posthog/events.ts
// Single source of truth for every PostHog event name and its closed property
// allowlist. No runtime imports (usable from both client and server bundles).
//
// Every event fired anywhere in the app is a constant declared here. No event
// ever emits a property literally named `status`, `reason`, `code` or `amount`
// (compound names like `kyc_status` or `reason_code` are fine, the literal bare
// word is not). Money properties are always `amount_bucket` (client) or
// `amount_minor` (server), never `amount`.

export const EVENTS = {
  // A. Consent / SDK — no dedicated named events beyond the gate itself.

  // C. Signup / auth
  SIGNUP_START: "signup_start",
  SIGNUP_EMAIL_ENTERED: "signup_email_entered",
  SIGNUP_SUBMIT: "signup_submit",
  SIGNUP_FAILED: "signup_failed",
  SIGNUP_SUCCESS: "signup_success",
  EMAIL_VERIFICATION_SENT: "email_verification_sent",
  EMAIL_VERIFICATION_COMPLETED: "email_verification_completed",
  LOGIN: "login",
  LOGIN_FAILED: "login_failed",
  TWO_FACTOR_PROMPTED: "two_factor_prompted",
  TWO_FACTOR_COMPLETED: "two_factor_completed",
  LOGOUT: "logout",

  // C. KYB wizard
  ONBOARDING_STARTED: "onboarding_started",
  ONBOARDING_STEP_VIEWED: "onboarding_step_viewed",
  ONBOARDING_STEP_COMPLETED: "onboarding_step_completed",
  ONBOARDING_STEP_BACK: "onboarding_step_back",
  ONBOARDING_STEP_FAILED: "onboarding_step_failed",
  ONBOARDING_DOCUMENT_UPLOADED: "onboarding_document_uploaded",
  ONBOARDING_SUBMITTED: "onboarding_submitted",
  ONBOARDING_SUBMIT_FAILED: "onboarding_submit_failed",
  ONBOARDING_COMPLETED: "onboarding_completed",

  // C. Marketing / CTAs
  CTA_CLICK: "cta_click",
  PRICING_VIEW: "pricing_view",
  PRODUCT_VIEW: "product_view",
  DOCS_VIEW: "docs_view",
  DEMO_REQUESTED: "demo_requested",
  OUTBOUND_CLICK: "outbound_click",

  // C. Form errors / feature gates
  FORM_ERROR: "form_error",
  FEATURE_BLOCKED: "feature_blocked",

  // C. Payment / ramping funnels (explicit-events-only surfaces)
  CHECKOUT_STARTED: "checkout_started",
  QUOTE_REQUESTED: "quote_requested",
  PAYMENT_METHOD_SELECTED: "payment_method_selected",
  PAYMENT_STARTED: "payment_started",
  PURCHASE_SUCCESS: "purchase_success",
  PURCHASE_FAILED: "purchase_failed",
  RAMP_STARTED: "ramp_started",
  RAMP_COMPLETED: "ramp_completed",
  RAMP_FAILED: "ramp_failed",

  // C. KYC (Sumsub)
  KYC_STARTED: "kyc_started",
  KYC_SUBMITTED: "kyc_submitted",

  // F. Server-side lifecycle
  KYC_APPROVED: "kyc_approved",
  KYC_REJECTED: "kyc_rejected",
  KYB_APPROVED: "kyb_approved",
  KYB_REAPPROVED: "kyb_reapproved",
  KYB_REJECTED: "kyb_rejected",
  KYB_RESUBMISSION_REQUIRED: "kyb_resubmission_required",
  FIRST_API_KEY_CREATED: "first_api_key_created",
  FIRST_PAYLINK_CREATED: "first_paylink_created",
  FIRST_PAYMENT_RECEIVED: "first_payment_received",
  FIRST_PAYOUT_REQUESTED: "first_payout_requested",
  ACTIVATED: "activated",

  // H. Error tracking
  ERROR_BOUNDARY_SHOWN: "error_boundary_shown",
  CLIENT_ERROR: "client_error",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// The closed allowlist shared by every server-side money event
// (`first_payment_received`, `first_paylink_created`).
const MONEY_EVENT_PROPERTIES = [
  "amount_minor",
  "currency",
  "method",
  "provider",
  "asset",
  "chain",
  "$source",
] as const;

// The closed allowlist shared by every client payment/ramping event.
const PAYMENT_FUNNEL_PROPERTIES = [
  "amount_bucket",
  "currency",
  "asset",
  "provider",
  "method",
  "error_code",
] as const;

const SIGNUP_LIFECYCLE_PROPERTIES = ["signup_method", "country"] as const;

// Per-event closed property allowlist. `pickAllowed` (properties.ts) drops any
// key not listed for its event.
export const EVENT_PROPERTIES: Record<EventName, readonly string[]> = {
  [EVENTS.SIGNUP_START]: SIGNUP_LIFECYCLE_PROPERTIES,
  [EVENTS.SIGNUP_EMAIL_ENTERED]: SIGNUP_LIFECYCLE_PROPERTIES,
  [EVENTS.SIGNUP_SUBMIT]: SIGNUP_LIFECYCLE_PROPERTIES,
  [EVENTS.SIGNUP_FAILED]: [...SIGNUP_LIFECYCLE_PROPERTIES, "reason_code", "stage"],
  [EVENTS.SIGNUP_SUCCESS]: SIGNUP_LIFECYCLE_PROPERTIES,
  [EVENTS.EMAIL_VERIFICATION_SENT]: ["$source"],
  [EVENTS.EMAIL_VERIFICATION_COMPLETED]: ["hours_since_signup", "$source"],
  [EVENTS.LOGIN]: [],
  [EVENTS.LOGIN_FAILED]: ["reason_code"],
  [EVENTS.TWO_FACTOR_PROMPTED]: ["method"],
  [EVENTS.TWO_FACTOR_COMPLETED]: ["method"],
  [EVENTS.LOGOUT]: [],

  [EVENTS.ONBOARDING_STARTED]: ["step_key", "step_name", "resumed", "application_status"],
  [EVENTS.ONBOARDING_STEP_VIEWED]: ["step_key", "step_name"],
  [EVENTS.ONBOARDING_STEP_COMPLETED]: ["step_key", "step_name", "duration_ms"],
  [EVENTS.ONBOARDING_STEP_BACK]: ["step_key", "step_name", "duration_ms"],
  [EVENTS.ONBOARDING_STEP_FAILED]: ["invalid_steps", "invalid_fields"],
  [EVENTS.ONBOARDING_DOCUMENT_UPLOADED]: ["doc_type"],
  [EVENTS.ONBOARDING_SUBMITTED]: ["total_duration_ms", "channel", "$source"],
  [EVENTS.ONBOARDING_SUBMIT_FAILED]: ["reason_code", "missing_document_types"],
  [EVENTS.ONBOARDING_COMPLETED]: ["days_since_signup", "$source"],

  [EVENTS.CTA_CLICK]: ["placement", "cta_label", "cta_destination", "article_slug", "translated"],
  [EVENTS.PRICING_VIEW]: ["plan_shown"],
  [EVENTS.PRODUCT_VIEW]: ["product", "path"],
  [EVENTS.DOCS_VIEW]: ["doc_section"],
  [EVENTS.DEMO_REQUESTED]: ["source_page"],
  [EVENTS.OUTBOUND_CLICK]: ["href", "link_text", "translated"],

  [EVENTS.FORM_ERROR]: ["form", "field", "error_code"],
  [EVENTS.FEATURE_BLOCKED]: ["feature", "reason_code"],

  [EVENTS.CHECKOUT_STARTED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.QUOTE_REQUESTED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.PAYMENT_METHOD_SELECTED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.PAYMENT_STARTED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.PURCHASE_SUCCESS]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.PURCHASE_FAILED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.RAMP_STARTED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.RAMP_COMPLETED]: PAYMENT_FUNNEL_PROPERTIES,
  [EVENTS.RAMP_FAILED]: PAYMENT_FUNNEL_PROPERTIES,

  [EVENTS.KYC_STARTED]: ["flow"],
  [EVENTS.KYC_SUBMITTED]: ["flow"],

  [EVENTS.KYC_APPROVED]: ["$source"],
  [EVENTS.KYC_REJECTED]: ["reason_code", "$source"],
  [EVENTS.KYB_APPROVED]: ["reason_code", "days_in_review", "$source"],
  [EVENTS.KYB_REAPPROVED]: ["reason_code", "days_in_review", "$source"],
  [EVENTS.KYB_REJECTED]: ["reason_code", "$source"],
  [EVENTS.KYB_RESUBMISSION_REQUIRED]: ["reason_code", "$source"],
  [EVENTS.FIRST_API_KEY_CREATED]: ["$source"],
  [EVENTS.FIRST_PAYLINK_CREATED]: MONEY_EVENT_PROPERTIES,
  [EVENTS.FIRST_PAYMENT_RECEIVED]: MONEY_EVENT_PROPERTIES,
  // A crypto withdrawal has no ISO exponent, so no amount travels.
  [EVENTS.FIRST_PAYOUT_REQUESTED]: ["asset", "chain", "$source"],
  [EVENTS.ACTIVATED]: ["days_since_signup", "$source"],

  [EVENTS.ERROR_BOUNDARY_SHOWN]: ["error_name", "digest", "route", "translated"],
  [EVENTS.CLIENT_ERROR]: ["error_name", "translated"],
};
